import { StackScreenProps } from "@react-navigation/stack";
import {
  FlatActionList,
  ModalCloseButton,
  Page,
} from "@towersystems/roam-common-native";
import { useEffect, useRef, useState } from "react";
import { RegisterRootParamList } from "../../../../types";
import Product from "../../../../utilities/data/model/Product";
import { useVariantPicker } from "../../utils/variant-picker/use-variant-picker";
import { useDatabase, useGetEntity } from "../../../../utilities/data/hooks";
import ProductKit from "../../../../utilities/data/model/ProductKit";
import ProductKitItem from "../../../../utilities/data/model/ProductKitItem";
import { useAddItem } from "../../utils/sale-controller";

export type KitResolverModalScreenProps = StackScreenProps<
  RegisterRootParamList,
  "KitResolverModalScreen"
>;

type KitState = {
  product: Product;
  productKit: ProductKit;
  productKitItems: ProductKitItem[];
  componentsToResolve: ProductKitItem[];
  componentMap?: Map<
    string, // kit item id
    { productId: string; quantity: number; price: number }
  >;
};

export const KitResolverModalScreen = ({
  navigation,
  route,
}: KitResolverModalScreenProps) => {
  const { productId } = route.params;
  const addItem = useAddItem({ navigation });

  const database = useDatabase();

  const [kitState, setKitState] = useState<KitState>();

  async function handleProductSelected(product: Product) {
    if (!kitState) {
      return;
    }

    const item = kitState.productKitItems.find(
      (i) => i.product.id === product.variantParentId
    );

    if (!item) {
      return;
    }

    let newKitState: KitState | undefined = undefined;

    setKitState((prev) => {
      const newState = {
        ...prev!,
        componentMap: new Map(prev?.componentMap).set(item.id!, {
          productId: product.id,
          quantity: item?.quantity || 1,
          price: item?.price || 0,
        }),
      };

      newKitState = newState;
      return newState;
    });
  }

  const {
    beginVariantPicker,
    items,
    state: {
      product: variant,
      activeOption,
      activeOptionIndex,
      selectedOptionValues,
    },
    actions: { clearOptionValueAfterIndex },
  } = useVariantPicker({
    onProductSelected: handleProductSelected,
  });

  function getNextComponentToResolve(currentKitState?: KitState) {
    const { componentsToResolve, componentMap } = currentKitState || {};

    if (!componentsToResolve?.length) {
      return;
    }

    for (const component of componentsToResolve) {
      if (!componentMap?.has(component.id)) {
        return component;
      }
    }
  }

  function loadKitState() {
    database.collections
      .get<Product>("products")
      .find(productId)
      .then(async (product) => {
        if (!product.isKit) {
          return;
        }

        const productKit = await product.productKit.fetch();

        const productKitItems = await productKit.items.fetch();

        const componentsToResolve: ProductKitItem[] = [];

        for (const item of productKitItems) {
          const itemProduct = await item.product.fetch();
          if (itemProduct.hasVariants) {
            componentsToResolve.push(item);
          }
        }

        setKitState({
          product,
          productKit,
          productKitItems,
          componentsToResolve,
        });
      });
  }

  function setHeaders() {
    if (variant && activeOption) {
      navigation.setOptions({
        title:
          selectedOptionValues.length >= 1
            ? `${selectedOptionValues.join(" / ")} / Select ${
                activeOption.name
              }`
            : `Select ${activeOption.name}`,
        headerLeft: () => {
          if (activeOptionIndex <= 0) {
            return <ModalCloseButton />;
          } else {
            return (
              <ModalCloseButton
                type="Back"
                onPress={() => {
                  clearOptionValueAfterIndex(activeOptionIndex - 1);
                }}
              />
            );
          }
        },
      });
    }
  }

  useEffect(setHeaders, [variant?.id, activeOption, activeOptionIndex]);

  useEffect(loadKitState, [productId]);

  useEffect(() => {
    if (!kitState) {
      return;
    }

    const result = getNextComponentToResolve(kitState);

    if (!!result) {
      beginVariantPicker(result.product.id);
    } else {
      // then check if there are more components to resolve
      const result = getNextComponentToResolve(kitState);

      if (result) {
        beginVariantPicker(result.product.id);
        return;
      } else {
        // nothing more to do, we can add the kit to the sale (with selected component variants)
        addItem(productId, {
          skipVariantPicker: true,
          metadata: {
            components: Object.fromEntries(
              kitState?.componentMap?.entries() || []
            ),
          },
        })
          .then(() => {
            navigation.goBack();
          })
          .catch((e) => console.log(e));
      }
    }
  }, [kitState?.componentsToResolve, kitState?.componentMap]);

  return (
    <Page>
      <FlatActionList items={items} />
    </Page>
  );
};
